import React, { useState, useRef, useContext, useEffect } from 'react';
import TrackingContext from '../helpers/TrackingContext';
import { set } from 'js-cookie';
import MembershipService from '@xo-union/sdk-membership';
import MembershipModal from '@xo-union/tk-component-membership-modal';
import honeybadger from 'honeybadger-js';

interface Props {
  onAccountCreated: () => void;
  currentModal: string | null;
  onClose: () => void;
}

interface ModalRef {
  showError: (message: string) => void;
}

interface MembershipCredentials {
  email: string;
  password: string;
}

interface MembershipModalProps {
  valid: boolean;
  data: MembershipCredentials;
}

const membershipClient = new MembershipService();

const auth = async (authType: string, creds: MembershipCredentials) => {
  if (authType === 'signUp') {
    return await membershipClient.signUp(creds);
  }
  return membershipClient.logIn(creds);
};

const OnboardingMembershipModal: React.FC<Props> = ({ onAccountCreated, currentModal, onClose }) => {
  const [openModal, setOpenModal] = useState<string | null>(currentModal);
  const [inProgress, setInProgress] = useState(false);
  const modalRef = useRef<ModalRef>(null);
  const { track } = useContext(TrackingContext);

  useEffect(() => {
    currentModal && setOpenModal(currentModal);
  }, [currentModal]);

  useEffect(() => {
    !openModal && onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal]);

  const createAccount = async (authType: string, resp: MembershipModalProps) => {
    if (resp.valid) {
      setInProgress(true);
      try {
        const responseWrapper = await auth(authType, resp.data);
        set('xo-session-token', responseWrapper?.data?.session?.token, {
          path: '/',
          domain: '.theknot.com',
        });
        setOpenModal(null);
        onAccountCreated();
      } catch (error) {
        honeybadger.notify(error, { message: 'Membership modal auth failed', context: { error } });
        //@ts-ignore
        if (error?.userFacingError) {
          track('Error Message Displayed', {
            product: 'wedding websites',
            source: 'membership modal',
            //@ts-ignore
            message: error.userFacingError,
          });
          //@ts-ignore
          modalRef.current?.showError(error.userFacingError);
        }
        setInProgress(false);
      }
    }
  };

  return (
    <MembershipModal
      ref={modalRef}
      openModal={openModal}
      onClickLogIn={() => setOpenModal('LOG_IN')}
      onClickSignUp={() => setOpenModal('SIGN_UP')}
      onClose={() => setOpenModal(null)}
      inProgress={inProgress}
      onLogIn={(resp: MembershipModalProps) => createAccount('logIn', resp)}
      onSignUp={(resp: MembershipModalProps) => createAccount('signUp', resp)}
      metadata={{
        userAction: 'wedding website create',
        applicationName: 'guest-services',
      }}
    />
  );
};

export default OnboardingMembershipModal;
